'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';

export type LogoItem = { src: string; alt: string; url?: string };

export const PARTNER_LOGOS: LogoItem[] = [
  // Healthcare — lead the marquee
  { src: '/partners/fnhk.webp',                alt: 'Fakultní nemocnice Hradec Králové', url: 'https://www.fnhk.cz/en/' },
  { src: '/partners/fn-plzen.png',             alt: 'Fakultní nemocnice Plzeň',          url: 'https://karim.fnplzen.cz/cs/node/1759' },
  { src: '/partners/penta-hospitals.png',      alt: 'Penta Hospitals',                   url: 'https://www.pentahospitals.com' },
  { src: '/partners/nemocnice-nachod.png',     alt: 'Nemocnice Náchod',                  url: 'https://www.nemocnicenachod.cz' },
  { src: '/partners/logo-nem-rychnov.webp',    alt: 'Nemocnice Rychnov nad Kněžnou',     url: 'https://www.nemocnicerychnov.cz' },
  { src: '/partners/medicalc.png',             alt: 'Medicalc HIS',                      url: 'https://www.medicalc.cz' },
  { src: '/partners/euc.png',                  alt: 'EUC — European Healthcare Network', url: 'https://euc.cz/en/' },
  // Legal
  { src: '/partners/rowan-legal.png',          alt: 'Rowan Legal',                       url: 'https://rowan.legal/en/' },
  { src: '/partners/anywhere-legal.png',       alt: 'Anywhere Legal',                    url: 'https://anywhere.legal' },
  { src: '/partners/die-netzwerkpartner.webp', alt: 'Die Netzwerkpartner',              url: 'https://www.die-netzwerkpartner.de' },
  // Industry / enterprise
  { src: '/partners/eurowag.png',              alt: 'Eurowag',                           url: 'https://www.eurowag.com' },
  { src: '/partners/t-systems.png',            alt: 'T-Systems',                         url: 'https://www.t-systems.com' },
  { src: '/partners/heggel.png',               alt: 'Heggel',                            url: 'https://www.heggelgmbh.com' },
  { src: '/partners/rwe.png',                  alt: 'RWE',                               url: 'https://www.rwe.com' },
  { src: '/partners/aso-systems.png',          alt: 'ASO Systems',                       url: 'https://www.aso.cz' },
];

type Props = {
  /** Optional override of the default partner list. */
  logos?: LogoItem[];
  /**
   * CSS colour value used for the edge fade gradients. Defaults to the page
   * primary background so the marquee dissolves into its surroundings.
   */
  fadeColor?: string;
  /** Auto-scroll speed in pixels per second. */
  speed?: number;
};

/**
 * Drag-aware logo marquee. The track position is owned by a single offset
 * variable updated each animation frame; auto-scroll and user drag both feed
 * into the same number, so releasing a drag resumes auto-scroll seamlessly
 * from wherever the user let go.
 */
export function LogoMarquee({
  logos = PARTNER_LOGOS,
  fadeColor = 'var(--background-primary)',
  speed = 40,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const halfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    const half = halfRef.current;
    if (!wrapper || !track || !half) return;

    let halfWidth = 0;
    const measure = () => {
      const w = half.scrollWidth;
      if (w > 0) halfWidth = w; // never pin to 0 — a transient 0 would freeze the track
    };
    measure();
    let offset = 0;            // pixels scrolled (positive). Always wrapped to [0, halfWidth).
    let lastFrame = 0;
    let dragging = false;      // pointer is down (pauses auto-scroll)
    let moved = false;         // drag threshold crossed (suppresses the click)
    let dragStartX = 0;
    let dragStartOffset = 0;
    let pointerId: number | null = null;
    const DRAG_THRESHOLD = 5;  // px before a press becomes a drag — lets taps click through
    let rafId = 0;
    // Visibility gate — when the marquee is off-screen or the tab is hidden,
    // stop scheduling rAF entirely. A 60fps rAF loop on an off-screen
    // element still forces the compositor to tick and rebuild paint layers
    // for everything else on the page; on Firefox that translates into
    // sustained 60-90% CPU even when the user is reading static text below.
    let visible = true;
    let pageVisible = typeof document === 'undefined' ? true : !document.hidden;

    const wrap = (v: number) => {
      if (halfWidth <= 0) measure(); // mobile: scrollWidth can read 0 until layout settles
      if (halfWidth <= 0) return 0;
      let x = v % halfWidth;
      if (x < 0) x += halfWidth;
      return x;
    };

    const apply = () => {
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    };

    const tick = (now: number) => {
      const dt = lastFrame === 0 ? 0 : (now - lastFrame) / 1000;
      lastFrame = now;
      if (!dragging) {
        offset = wrap(offset + speed * dt);
        apply();
      }
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (rafId !== 0) return;
      lastFrame = 0;
      rafId = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (rafId === 0) return;
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const onResize = () => {
      measure();
    };

    // Keep the track width correct once images/fonts finish laying out. On
    // mobile, half.scrollWidth can read 0 during the effect's first pass; a
    // ResizeObserver re-measures as soon as real layout is known, so the
    // marquee doesn't stay frozen (offset wrapped against a 0 width froze both
    // auto-scroll and drag while taps still worked).
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => measure())
      : null;
    ro?.observe(half);

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== undefined && e.button !== 0) return;
      dragging = true;
      moved = false;
      pointerId = e.pointerId;
      dragStartX = e.clientX;
      dragStartOffset = offset;
      // Capture is deferred to the first real move (see onPointerMove) so a
      // plain tap still dispatches a native click to the logo link below.
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - dragStartX;
      if (!moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        moved = true;
        try { wrapper.setPointerCapture(e.pointerId); } catch {}
      }
      // Drag left → reveal logos to the right → offset increases.
      offset = wrap(dragStartOffset - dx);
      apply();
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const wasDrag = moved;
      dragging = false;
      moved = false;
      pointerId = null;
      lastFrame = 0; // reset dt baseline so resume doesn't snap forward
      try { wrapper.releasePointerCapture(e.pointerId); } catch {}
      if (wasDrag) {
        // Swallow the click the browser fires after a drag, so scrubbing the
        // row doesn't navigate to whichever logo the pointer was released on.
        const suppress = (ev: Event) => { ev.preventDefault(); ev.stopPropagation(); };
        wrapper.addEventListener('click', suppress, { capture: true, once: true });
        window.setTimeout(() => wrapper.removeEventListener('click', suppress, true), 60);
      }
    };

    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(entries => {
          for (const entry of entries) {
            visible = entry.isIntersecting;
          }
          if (visible && pageVisible) start(); else stop();
        }, { rootMargin: '128px' })
      : null;
    io?.observe(wrapper);

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (visible && pageVisible) start(); else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Don't synchronously start() — IntersectionObserver fires its initial
    // callback on the next microtask after observe(), which will start the
    // rAF loop iff the marquee is actually visible. This avoids a 60fps
    // composite tick during hydration when the user can't see it yet.
    // Fallback: if IO isn't supported, start immediately.
    if (!io) start();
    window.addEventListener('resize', onResize);
    wrapper.addEventListener('pointerdown', onPointerDown);
    wrapper.addEventListener('pointermove', onPointerMove);
    wrapper.addEventListener('pointerup', onPointerUp);
    wrapper.addEventListener('pointercancel', onPointerUp);
    wrapper.addEventListener('pointerleave', onPointerUp);

    return () => {
      stop();
      io?.disconnect();
      ro?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      wrapper.removeEventListener('pointerdown', onPointerDown);
      wrapper.removeEventListener('pointermove', onPointerMove);
      wrapper.removeEventListener('pointerup', onPointerUp);
      wrapper.removeEventListener('pointercancel', onPointerUp);
      wrapper.removeEventListener('pointerleave', onPointerUp);
    };
  }, [speed]);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing select-none touch-pan-y"
      style={{ touchAction: 'pan-y' }}
      aria-label="Partner logos"
    >
      <div
        className="absolute inset-y-0 left-0 w-[15%] z-10 pointer-events-none"
        style={{ background: `linear-gradient(to right, ${fadeColor}, transparent)` }}
      />
      <div
        className="absolute inset-y-0 right-0 w-[15%] z-10 pointer-events-none"
        style={{ background: `linear-gradient(to left, ${fadeColor}, transparent)` }}
      />
      <div ref={trackRef} className="flex w-max will-change-transform">
        {/* The track contains two identical halves so we can wrap the offset
            seamlessly: when offset === halfWidth, the second half occupies
            exactly the position the first half started in. */}
        <LogoHalf logos={logos} ref={halfRef} />
        <LogoHalf logos={logos} duplicate />
      </div>
    </div>
  );
}

const LogoHalf = function LogoHalf({
  logos,
  duplicate,
  ref,
}: {
  logos: LogoItem[];
  duplicate?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}) {
  const box = 'relative flex h-12 w-[150px] shrink-0 items-center justify-center';
  return (
    <div ref={ref} aria-hidden={duplicate} className="flex shrink-0 items-center gap-14 pr-14">
      {logos.map(({ src, alt, url }) => {
        const img = (
          <Image
            src={src}
            alt={alt}
            fill
            sizes="150px"
            loading="eager"
            draggable={false}
            className="object-contain"
          />
        );
        return url ? (
          <a
            key={src}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={alt}
            tabIndex={duplicate ? -1 : undefined}
            draggable={false}
            className={`${box} transition-opacity duration-200 hover:opacity-70`}
          >
            {img}
          </a>
        ) : (
          <div key={src} className={`${box} pointer-events-none`}>
            {img}
          </div>
        );
      })}
    </div>
  );
};
